'use client';

import isEqual from 'lodash/isEqual';
// types
import { useRouter } from 'next/navigation';
import { useState, useCallback } from 'react';

import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
// @mui
import TableBody from '@mui/material/TableBody';
import IconButton from '@mui/material/IconButton';
import TableContainer from '@mui/material/TableContainer';

// locales
import { useTranslate } from 'src/locales';

//
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import {
  useTable,
  emptyRows,
  TableNoData,
  getComparator,
  TableEmptyRows,
  TableHeadCustom,
  TableSelectedAction,
  TablePaginationCustom,
} from 'src/components/table';

import { _myAgents } from '../../../../../_mock/_affiliate';
import { useBoolean } from '../../../../../hooks/use-boolean';
import ProfileMyAgentTableRow from './profile-my-agent-table-row';
import { ConfirmDialog } from '../../../../../components/custom-dialog';
import ProfileMyAgentTableToolbar from './profile-my-agent-table-toolbar';
// ----------------------------------------------------------------------

const defaultFilters: any = {
  name: '',
  role: [],
  assigned: [],
  merchant: [],
  status: 'all',
  origin: [],
  priority: [],
  startDate: null,
  endDate: null,
};

type Props = {
  page?: string;
};

// ----------------------------------------------------------------------

export default function ProfileMyAgentTable({ page }: Props) {
  const { t } = useTranslate();

  const table: any = useTable();

  const router = useRouter();

  const confirmDeleteRow = useBoolean();

  const [tableData, setTableData] = useState(_myAgents);

  const [filters, setFilters] = useState(defaultFilters);

  const TABLE_HEAD = [
    { id: '', label: t('') },
    { id: 'agentsName', label: t('Agents name') },
    { id: 'smbAccount', label: t('SMB account') },
    { id: 'activity', label: t('Activity') },
    { id: 'date', label: t('Date') },
    { id: 'status', label: t('Status') },
    { id: 'creator', label: t('Creator') },
    { id: '', label: t('') },
  ];

  const dateError =
    filters.startDate && filters.endDate
      ? filters.startDate.getTime() > filters.endDate.getTime()
      : false;

  const dataFiltered = applyFilter({
    inputData: tableData,
    comparator: getComparator(table.order, table.orderBy),
    filters,
    dateError,
  });

  const dataInPage = dataFiltered.slice(
    table.page * table.rowsPerPage,
    table.page * table.rowsPerPage + table.rowsPerPage
  );

  const denseHeight = table.dense ? 52 : 72;

  const canReset = !isEqual(defaultFilters, filters);

  const notFound = (!dataFiltered.length && canReset) || !dataFiltered.length;

  const handleFilters = useCallback(
    (name: string, filterValue: any) => {
      table.onResetPage();
      setFilters((prevState: any) => ({
        ...prevState,
        [name]: filterValue,
      }));
    },
    [table]
  );

  const handleDeleteRow = useCallback(
    (id: string) => {
      const deleteRow = tableData.filter((row: any) => row.id !== id);
      setTableData(deleteRow);

      table.onUpdatePageDeleteRow(dataInPage.length);
    },
    [dataInPage.length, table, tableData]
  );

  const handleDeleteRows = useCallback(() => {
    const deleteRows = tableData.filter((row: any) => !table.selected.includes(row.id));
    setTableData(deleteRows);

    table.onUpdatePageDeleteRows({
      totalRows: tableData.length,
      totalRowsInPage: dataInPage.length,
      totalRowsFiltered: dataFiltered.length,
    });
  }, [dataFiltered.length, dataInPage.length, table, tableData]);

  const handleEditRow = useCallback(
    (id: string) => {
      // router.push(paths.dashboard.affiliates.agents.edit(id));
    },
    [router]
  );

  const handleFilterStatus = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      handleFilters('status', newValue);
    },
    [handleFilters]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  return (
    <>
      <Card>
        <ProfileMyAgentTableToolbar filters={filters} onFilters={handleFilters} dateError={false} />
        <TableContainer
          sx={{
            position: 'relative',
            overflow: 'unset',
            '& .MuiTableHead-root': { bgcolor: '#eef1f4' },
          }}
        >
          <TableSelectedAction
            dense={table.dense}
            numSelected={table.selected.length}
            rowCount={tableData.length}
            onSelectAllRows={(checked) =>
              table.onSelectAllRows(
                checked,
                tableData.map((row: any) => row.id)
              )
            }
            action={
              <Tooltip title={t('Delete')}>
                <IconButton color="primary" onClick={confirmDeleteRow.onTrue}>
                  <Iconify icon="solar:trash-bin-trash-bold" />
                </IconButton>
              </Tooltip>
            }
          />

          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom
                order={table.order}
                orderBy={table.orderBy}
                headLabel={TABLE_HEAD}
                rowCount={tableData.length}
                numSelected={table.selected.length}
                onSort={table.onSort}
                onSelectAllRows={(checked) =>
                  table.onSelectAllRows(
                    checked,
                    tableData.map((row: any) => row.id)
                  )
                }
              />

              <TableBody>
                {dataFiltered
                  .slice(
                    table.page * table.rowsPerPage,
                    table.page * table.rowsPerPage + table.rowsPerPage
                  )
                  .map((row: any) => (
                    <ProfileMyAgentTableRow
                      key={row.id}
                      row={row}
                      selected={table.selected.includes(row.id)}
                      onSelectRow={() => table.onSelectRow(row.id)}
                      onDeleteRow={() => handleDeleteRow(row.id)}
                      onEditRow={() => handleEditRow(row.id)}
                      page={page}
                    />
                  ))}

                <TableEmptyRows
                  height={denseHeight}
                  emptyRows={emptyRows(table.page, table.rowsPerPage, tableData.length)}
                />

                <TableNoData notFound={notFound} />
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={dataFiltered.length}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          //
          dense={table.dense}
          onChangeDense={table.onChangeDense}
        />
      </Card>
      <ConfirmDialog
        open={confirmDeleteRow.value}
        onClose={confirmDeleteRow.onFalse}
        title={t('Delete')}
        content={
          <>
            {t('Are you sure want to delete')} <strong> {table.selected.length} </strong>{' '}
            {t('items?')}
          </>
        }
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              handleDeleteRows();
              confirmDeleteRow.onFalse();
            }}
          >
            {t('Delete')}
          </Button>
        }
      />
    </>
  );
}

// ----------------------------------------------------------------------

function applyFilter({
  inputData,
  comparator,
  filters,
  dateError,
}: {
  inputData: any;
  comparator: (a: any, b: any) => number;
  filters: any;
  dateError: boolean;
}) {
  const { invoiceNumber, status, startDate, endDate } = filters;

  // const stabilizedThis = inputData.map((el, index) => [el, index] as const);

  // stabilizedThis.sort((a, b) => {
  //   const order = comparator(a[0], b[0]);
  //   if (order !== 0) return order;
  //   return a[1] - b[1];
  // });

  // inputData = stabilizedThis.map((el) => el[0]);

  // if (invoiceNumber) {
  //   inputData = inputData.filter(
  //     (customer) =>
  //       customer?.invoiceNumber.toLowerCase().indexOf(invoiceNumber.toLowerCase()) !== -1,
  //   );
  // }

  // if (status !== 'all') {
  //   inputData = inputData.filter((customer) => customer.status === status);
  // }

  // if (!dateError) {
  //   if (startDate && endDate) {
  //     inputData = inputData.filter(
  //       (invoice) => fTimestamp(invoice.create) >= fTimestamp(startDate) && fTimestamp(invoice.due) <= fTimestamp(endDate),
  //     );
  //   }
  // }

  return inputData;
}
